import React, { useState, useRef, useEffect } from "react";
import { useRequestProcessor } from "../../requestProcessor";
import { checkPolicyStatus, acceptPolicy } from "../../queryfunctions/queryfunctions";
import { getAuthToken, setAlertModal } from "../../atomstore/serviceAtoms";
import { useAtom } from "jotai";
import { activeChildDetail } from "../../atomstore/store";

const POLICY_CONTENT = `


<p class="mb-5 leading-relaxed text-gray-700 text-base">
As part of our commitment to maintaining operational excellence and billing transparency across all centres, we will be implementing the following policy refinements effective <strong>1<sup>st</sup> April 2026</strong>.
</p>

<p class="mb-6 leading-relaxed text-gray-700 text-base">
These updates are being introduced uniformly across centres to ensure clarity, predictability, and consistency in parent experience.
</p>

<h3 class="text-lg font-bold mt-7 mb-3.5 text-gray-900 border-b-2 border-gray-200 pb-2">Program Change</h3>
<p class="mb-4 leading-relaxed text-gray-700 text-base">
To ensure accuracy in fee alignment and eliminate mid-cycle disruptions, program changes will now follow a structured billing framework.
</p>
<ul class="mb-5 pl-6 leading-relaxed text-gray-700 text-base list-disc">
    <li class="mb-2.5">All program modifications will take effect from the next billing cycle.</li>
    <li class="mb-2.5">Requests must be submitted before the 5<sup>th</sup> of the month or prior to invoice payment, whichever occurs earlier.</li>
    <li class="mb-2.5">Mid-cycle billing reversals and manual adjustments will no longer be accommodated.</li>
</ul>
<p class="mb-6 leading-relaxed text-gray-700 text-base italic">
This ensures precision and uniformity across centres.
</p>

<h3 class="text-lg font-bold mt-7 mb-3.5 text-gray-900 border-b-2 border-gray-200 pb-2">Pause Policy</h3>
<p class="mb-4 leading-relaxed text-gray-700 text-base">
While flexibility remains important, operational continuity is equally essential in a structured academic environment. The pause framework has therefore been standardised.
</p>
<ul class="mb-5 pl-6 leading-relaxed text-gray-700 text-base list-disc">
    <li class="mb-2.5">A pause may be availed once every 90 days, for a fixed duration of 30 days.</li>
    <li class="mb-2.5">Pauses may commence from any date within the month; however, backdated requests will not be accepted.</li>
    <li class="mb-2.5">The applicable pause fee has been revised to <strong>50% of the prevailing fee</strong>.</li>
    <li class="mb-2.5">Consecutive pauses across adjoining months will not be permitted.</li>
    <li class="mb-2.5">The next pause can be availed after <strong>60 days from the last pause end date</strong>.</li>
</ul>
<p class="mb-6 leading-relaxed text-gray-700 text-base italic">
This ensures balance between parental flexibility and classroom stability.
</p>

<h3 class="text-lg font-bold mt-7 mb-3.5 text-gray-900 border-b-2 border-gray-200 pb-2">Centre Transfer</h3>
<p class="mb-4 leading-relaxed text-gray-700 text-base">
Centre transfers are now fully system-driven to provide a seamless transition experience.
</p>
<ul class="mb-5 pl-6 leading-relaxed text-gray-700 text-base list-disc">
    <li class="mb-2.5">Transfers will be auto-approved, with billing at the new centre commencing from the following day.</li>
    <li class="mb-2.5">Where a transfer is made to a lower-fee centre, <strong>no credits will be issued</strong>.</li>
    <li class="mb-2.5">Where a transfer is made to a higher-fee centre, <strong>pro-rated charges will apply</strong>.</li>
</ul>
<p class="mb-6 leading-relaxed text-gray-700 text-base italic">
Ensuring clarity and finality in transition processes.
</p>

<h3 class="text-lg font-bold mt-7 mb-3.5 text-gray-900 border-b-2 border-gray-200 pb-2">Annual Fee</h3>
<ul class="mb-5 pl-6 leading-relaxed text-gray-700 text-base list-disc">
    <li class="mb-2.5">Effective <strong>1 April 2026</strong>, the annual fee has been revised to <strong>₹16,000</strong>.</li>
    <li class="mb-2.5">From the second year onward, the annual fee will be <strong>fully non-refundable</strong>, with no proration applicable.</li>
    <li class="mb-2.5">Parents who prefer not to continue under long-term commitment may opt for the <strong>V2 (Short-Term) plan</strong>.</li>
</ul>
<p class="mb-6 leading-relaxed text-gray-700 text-base italic">
This provides transparency in enrolment commitments and long-term planning.
</p>

<h3 class="text-lg font-bold mt-7 mb-3.5 text-gray-900 border-b-2 border-gray-200 pb-2">Implementation</h3>
<p class="mb-4 leading-relaxed text-gray-700 text-base">
All changes outlined above will take effect from <strong>1 April 2026</strong>, unless otherwise specified.
</p>
<p class="mb-8 leading-relaxed text-gray-700 text-base">
These refinements are designed to maintain a disciplined, transparent, and premium operational environment across all centres.
</p>

<p class="mt-8 mb-8 leading-relaxed text-gray-500 italic text-sm p-4 bg-gray-50 border-l-4 border-blue-500 rounded">
By accepting these terms, you acknowledge that you have read, understood, and agree to be bound by this updated billing policy effective 1<sup>st</sup> April 2026.
</p>
`;

const PolicyModal = () => {
    const { useQueryWrapper, useMutationWrapper } = useRequestProcessor();
    const [activeChild] = useAtom(activeChildDetail);
    const [showModal, setShowModal] = useState(false);
    const [isScrolledToBottom, setIsScrolledToBottom] = useState(false);
    const scrollContainerRef = useRef(null);

    const authToken = getAuthToken();
    const shouldCheckPolicy = authToken && authToken !== '""' && activeChild?.id;


    // Check policy status using useQuery
    useQueryWrapper(
        ["checkPolicyStatus", activeChild?.id],
        () => checkPolicyStatus(),
        {
            enabled: !!shouldCheckPolicy,
            refetchOnWindowFocus: false,
            refetchOnMount: false,
            onSuccess: (data) => {
                if (data && (data.policy_accepted === null || data.policy_accepted === "" || data.policy_accepted === "N")) {
                    setShowModal(true);
                }
            },
        }
    );

    // Accept policy using useMutation
    const acceptPolicyMutation = useMutationWrapper(
        ["acceptPolicy"],
        () => acceptPolicy(),
        {
            onSuccess: (response) => {
                if (response && response.status === "success") {
                    console.log("[PolicyModal] Policy accepted successfully:", response.message);
                    setShowModal(false);
                } else if (response && response.status === "error") {
                    console.error("[PolicyModal] Policy acceptance error:", response.message);
                    setAlertModal({
                        state: true,
                        type: "Error",
                        message: response.message || "Failed to accept policy. Please try again.",
                        isConfirm: false,
                        onOkPressed: () => { },
                        onCancelPressed: () => { },
                    });
                } else {
                    console.error("[PolicyModal] Unexpected response:", response);
                    setAlertModal({
                        state: true,
                        type: "Error",
                        message: "Failed to accept policy. Please try again.",
                        isConfirm: false,
                        onOkPressed: () => { },
                        onCancelPressed: () => { },
                    });
                }
            },
            onError: (error) => {
                console.error("[PolicyModal] Error accepting policy:", error);
                setAlertModal({
                    state: true,
                    type: "Error",
                    message: "Failed to accept policy. Please try again.",
                    isConfirm: false,
                    onOkPressed: () => { },
                    onCancelPressed: () => { },
                });
            },
        }
    );

    // Prevent background scroll / layout jitter when modal is open (especially on iOS)
    useEffect(() => {
        if (!showModal) return;

        if (typeof document === "undefined") return;

        const originalOverflow = document.body.style.overflow;
        const originalPosition = document.body.style.position;
        const originalWidth = document.body.style.width;

        document.body.style.overflow = "hidden";
        document.body.style.position = "fixed";
        document.body.style.width = "100%";

        return () => {
            document.body.style.overflow = originalOverflow;
            document.body.style.position = originalPosition;
            document.body.style.width = originalWidth;
        };
    }, [showModal]);

    const handleScroll = () => {
        const container = scrollContainerRef.current;
        if (!container) return;

        const { scrollTop, scrollHeight, clientHeight } = container;
        const scrollPercentage = (scrollTop + clientHeight) / scrollHeight;


        // Enable button when 95% scrolled
        if (scrollPercentage >= 0.95) {
            setIsScrolledToBottom(true);
        } else {
            setIsScrolledToBottom(false);
        }
    };

    const handleAccept = () => {
        if (!activeChild?.id) {
            console.error("[PolicyModal] No active child found");
            return;
        }

        acceptPolicyMutation.mutate();
    };

    if (!showModal) return null;

    return (
        <div className="fixed inset-0 z-[99998] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4">
            <div className="w-full max-w-2xl bg-white rounded-2xl shadow-2xl overflow-hidden max-h-[90vh] flex flex-col">
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-blue-600 to-blue-700">
                    <h2 className="text-xl font-bold text-white">
                        New Billing Policy Terms
                    </h2>
                </div>

                {/* Scrollable Content */}
                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    className="flex-1 overflow-y-auto px-6 py-4 scrollbar-thin scrollbar-thumb-slate-300 scrollbar-track-slate-100"
                >
                    <div
                        className="prose prose-base max-w-none text-gray-700 leading-relaxed"
                        dangerouslySetInnerHTML={{ __html: POLICY_CONTENT }}
                    />
                </div>

                {/* Footer with Accept Button */}
                <div className="px-6 py-4 border-t bg-gray-50">
                    <button
                        onClick={handleAccept}
                        disabled={!isScrolledToBottom || acceptPolicyMutation.isLoading}
                        className={`
                            w-full py-3 px-6 rounded-lg font-semibold text-white
                            transition-all duration-200
                            ${isScrolledToBottom && !acceptPolicyMutation.isLoading
                                ? 'bg-blue-600 hover:bg-blue-700 active:scale-[0.98] shadow-lg hover:shadow-xl'
                                : 'bg-gray-400 cursor-not-allowed opacity-60'
                            }
                        `}
                    >
                        {acceptPolicyMutation.isLoading ? 'Processing...' : 'Accept & Continue'}
                    </button>
                    {!isScrolledToBottom && (
                        <p className="text-xs text-gray-500 text-center mt-2">
                            Please scroll to the bottom to accept
                        </p>
                    )}
                </div>
            </div>

        </div>
    );
};

export default PolicyModal;

